/**
 * Icarus Personalise - Ultra-Fast Product Card Personalisation Engine
 * Client-side high-performance canvas renderer for collection grids, search modals, categories, and recommend cards
 * Features: In-memory image & font caching, instant rendered DataURL caching, pre-warming, and zero-lag updates
 */

(function() {
  'use strict';

  // Prevent multiple initializations if script tag is included multiple times
  if (window.__icarusPersonaliseInitialized) {
    if (typeof window.applyIcarusPersonalisation === 'function') {
      window.applyIcarusPersonalisation(true);
    }
    return;
  }
  window.__icarusPersonaliseInitialized = true;

  // Global In-Memory Caches
  const fontCache = new Map();
  const imageElementCache = new Map();
  const renderedDataUrlCache = new Map();

  // Shared reusable offscreen canvas for zero GC allocation overhead
  let sharedCanvas = null;
  let sharedCtx = null;

  function getSharedCanvas(width, height) {
    if (!sharedCanvas) {
      sharedCanvas = document.createElement('canvas');
      sharedCtx = sharedCanvas.getContext('2d', { willReadFrequently: false });
    }
    sharedCanvas.width = width;
    sharedCanvas.height = height;
    return { canvas: sharedCanvas, ctx: sharedCtx };
  }

  /**
   * Fast font loading using FontFace API (without waiting for document.fonts.ready)
   */
  async function loadCustomFont(fontUrl) {
    if (!fontUrl) return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

    if (fontUrl.startsWith('//')) {
      fontUrl = 'https:' + fontUrl;
    }

    if (fontCache.has(fontUrl)) {
      return fontCache.get(fontUrl);
    }

    const fontPromise = (async () => {
      try {
        const fontName = 'IcarusCardFont_' + Math.abs(hashCode(fontUrl));
        const fontFace = new FontFace(fontName, `url(${fontUrl})`, { weight: 'bold' });
        const loadedFace = await fontFace.load();
        document.fonts.add(loadedFace);
        return fontName;
      } catch (err) {
        return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
      }
    })();

    fontCache.set(fontUrl, fontPromise);
    return fontPromise;
  }

  function hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  }

  /**
   * Draws arched/curved text on HTML5 canvas along an upward arc
   */
  function drawCurvedText(ctx, text, centerX, centerY, radius) {
    if (!text || radius <= 0) {
      ctx.fillText(text, centerX, centerY);
      return;
    }
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const charWidths = [];
    let totalWidth = 0;
    for (let i = 0; i < text.length; i++) {
      const w = ctx.measureText(text[i]).width;
      charWidths.push(w);
      totalWidth += w;
    }

    const totalAngle = totalWidth / radius;
    let currentAngle = -totalAngle / 2;
    const circleCenterY = centerY + radius;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charAngle = charWidths[i] / radius;
      const midAngle = currentAngle + charAngle / 2;

      ctx.save();
      ctx.translate(centerX, circleCenterY);
      ctx.rotate(midAngle);
      ctx.translate(0, -radius);
      ctx.fillText(char, 0, 0);
      ctx.restore();

      currentAngle += charAngle;
    }

    ctx.restore();
  }

  /**
   * Get or load base HTMLImageElement with in-memory caching
   */
  function getOrLoadImage(src) {
    if (!src) return Promise.resolve(null);

    if (imageElementCache.has(src)) {
      const cached = imageElementCache.get(src);
      if (cached instanceof HTMLImageElement && cached.complete && cached.naturalWidth > 0) {
        return Promise.resolve(cached);
      }
      if (cached instanceof Promise) {
        return cached;
      }
    }

    const imgPromise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        imageElementCache.set(src, img);
        resolve(img);
      };
      img.onerror = function() {
        resolve(null);
      };
      img.src = src;
    });

    imageElementCache.set(src, imgPromise);
    return imgPromise;
  }

  /**
   * Pre-warm image cache for all product cards currently in DOM
   */
  window.prewarmIcarusPersonalisation = function() {
    try {
      const cards = document.querySelectorAll('[data-icarus-card], [data-product-card]');
      cards.forEach(card => {
        const front = card.getAttribute('data-front-image');
        const back = card.getAttribute('data-back-image');
        const font = card.getAttribute('data-font-file');
        if (front) getOrLoadImage(front);
        if (back) getOrLoadImage(back);
        if (font) loadCustomFont(font);
      });
    } catch (e) {}
  };

  /**
   * Render customisation onto base jersey image with instant DataURL caching
   */
  async function renderPersonalisedDataUrl(baseSrc, view, card, prefs, fontFamily, textColor) {
    if (!baseSrc) return null;

    const prefsKey = `${prefs.name || ''}_${prefs.number || ''}_${prefs.initials || ''}`;
    const cacheKey = `${baseSrc}__${view}__${prefsKey}__${fontFamily}__${textColor}`;

    // Instant return if already rendered in memory
    if (renderedDataUrlCache.has(cacheKey)) {
      return renderedDataUrlCache.get(cacheKey);
    }

    const img = await getOrLoadImage(baseSrc);
    if (!img) return null;

    try {
      const width = img.naturalWidth || 800;
      const height = img.naturalHeight || 1067;
      const { ctx, canvas } = getSharedCanvas(width, height);

      if (!ctx) return null;

      // Draw flat jersey base
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const cqw = width / 100;

      if (view === 'front') {
        const frontInitialsEnabled = card.getAttribute('data-front-initials-enabled') === 'true';
        const frontNumberEnabled = card.getAttribute('data-front-number-enabled') === 'true';

        // Draw Initials
        if (frontInitialsEnabled && prefs.initials) {
          const initialsTop = parseFloat(card.getAttribute('data-front-initials-top')) || 25;
          const initialsLeft = parseFloat(card.getAttribute('data-front-initials-left')) || 35;
          const initialsFontSize = parseFloat(card.getAttribute('data-front-initials-font-size')) || 70;

          const x = (initialsLeft / 100) * width;
          const y = (initialsTop / 100) * height;
          const fontSizePx = (initialsFontSize / 4.4) * cqw;

          ctx.font = `bold ${Math.round(fontSizePx)}px ${fontFamily}`;
          ctx.fillText(prefs.initials.toUpperCase(), x, y);
        }

        // Draw Front Number
        if (frontNumberEnabled && prefs.number) {
          const frontNumberTop = parseFloat(card.getAttribute('data-front-number-top')) || 25;
          const frontNumberLeft = parseFloat(card.getAttribute('data-front-number-left')) || 50;
          const frontNumberFontSize = parseFloat(card.getAttribute('data-front-number-font-size')) || 90;

          const x = (frontNumberLeft / 100) * width;
          const y = (frontNumberTop / 100) * height;
          const fontSizePx = (frontNumberFontSize / 4.4) * cqw;

          ctx.font = `bold ${Math.round(fontSizePx)}px ${fontFamily}`;
          ctx.fillText(prefs.number.toUpperCase(), x, y);
        }
      } else if (view === 'back') {
        const nameEnabled = card.getAttribute('data-name-enabled') === 'true';
        const numberEnabled = card.getAttribute('data-number-enabled') === 'true';

        // Draw Name
        if ((nameEnabled || !card.hasAttribute('data-name-enabled')) && prefs.name) {
          const nameTop = parseFloat(card.getAttribute('data-name-top')) || 30;
          const nameLeft = parseFloat(card.getAttribute('data-name-left')) || 50;
          const nameFontSize = parseFloat(card.getAttribute('data-name-font-size')) || 80;
          const nameCurve = parseFloat(card.getAttribute('data-name-curve')) || 0;

          const x = (nameLeft / 100) * width;
          const y = (nameTop / 100) * height;
          const fontSizePx = (nameFontSize / 4.4) * cqw;
          const textToDraw = prefs.name.toUpperCase();

          ctx.font = `bold ${Math.round(fontSizePx)}px ${fontFamily}`;

          if (nameCurve > 0) {
            const radius = nameCurve <= 100 ? (width * 10000) / (nameCurve * 150) : (nameCurve * (width / 1000));
            drawCurvedText(ctx, textToDraw, x, y, radius);
          } else {
            ctx.fillText(textToDraw, x, y);
          }
        }

        // Draw Number
        if ((numberEnabled || !card.hasAttribute('data-number-enabled')) && prefs.number) {
          const numberTop = parseFloat(card.getAttribute('data-number-top')) || 36;
          const numberLeft = parseFloat(card.getAttribute('data-number-left')) || 50;
          const numberFontSize = parseFloat(card.getAttribute('data-number-font-size')) || 245;

          const x = (numberLeft / 100) * width;
          const y = (numberTop / 100) * height;
          const fontSizePx = (numberFontSize / 4.4) * cqw;

          ctx.font = `bold ${Math.round(fontSizePx)}px ${fontFamily}`;
          ctx.fillText(prefs.number.toUpperCase(), x, y);
        }
      }

      const resultDataUrl = canvas.toDataURL('image/jpeg', 0.93);
      renderedDataUrlCache.set(cacheKey, resultDataUrl);
      return resultDataUrl;
    } catch (err) {
      return null;
    }
  }

  /**
   * Personalises a single product card
   */
  async function personaliseCard(card, prefs) {
    if (!card || !prefs) return;

    const prefsKey = `${prefs.name || ''}_${prefs.number || ''}_${prefs.initials || ''}`;
    if (card.getAttribute('data-icarus-rendered-key') === prefsKey) {
      return;
    }

    const frontImgSrc = card.getAttribute('data-front-image');
    const backImgSrc = card.getAttribute('data-back-image');

    if (!frontImgSrc && !backImgSrc) return;

    const fontFile = card.getAttribute('data-font-file');
    const textColor = card.getAttribute('data-text-color') || '#000000';

    const fontFamily = await loadCustomFont(fontFile);

    const targetImg = card.querySelector('img[data-card-image]') || 
                      card.querySelector('[class*="icarus-product-card-image"]:not([class*="secondary"])') ||
                      card.querySelector('[class*="product-card-image"]:not([class*="secondary"])') ||
                      card.querySelector('[class*="recommend__image"]:not([class*="secondary"])') ||
                      card.querySelector('img');
    if (!targetImg) return;

    // 1. Primary View (Immediate Priority)
    const primarySrc = frontImgSrc || backImgSrc;
    const primaryView = frontImgSrc ? 'front' : 'back';

    renderPersonalisedDataUrl(primarySrc, primaryView, card, prefs, fontFamily, textColor).then(dataUrl => {
      if (dataUrl) {
        targetImg.src = dataUrl;
        targetImg.removeAttribute('srcset'); // Ensure high-res canvas is displayed without browser override
      }
    });

    // 2. Secondary View for Hover (Background Asynchronous)
    const hoverSrc = frontImgSrc ? backImgSrc : null;
    if (hoverSrc) {
      renderPersonalisedDataUrl(hoverSrc, 'back', card, prefs, fontFamily, textColor).then(dataUrl => {
        if (dataUrl) {
          let secondaryImg = card.querySelector('img[data-card-secondary-image]') || 
                             card.querySelector('[class*="icarus-product-card-image-secondary"]') ||
                             card.querySelector('[class*="product-card-image-secondary"]') ||
                             card.querySelector('[class*="recommend__image-secondary"]') ||
                             card.querySelector('img.secondary');

          if (!secondaryImg && targetImg.parentElement) {
            secondaryImg = document.createElement('img');
            secondaryImg.className = (targetImg.className || '').replace('image-', 'image-secondary-') + ' icarus-card-secondary-hover';
            secondaryImg.setAttribute('data-card-secondary-image', 'true');
            secondaryImg.setAttribute('loading', 'lazy');
            secondaryImg.style.position = 'absolute';
            secondaryImg.style.top = '0';
            secondaryImg.style.left = '0';
            secondaryImg.style.width = '100%';
            secondaryImg.style.height = '100%';
            secondaryImg.style.objectFit = 'cover';
            secondaryImg.style.opacity = '0';
            secondaryImg.style.transition = 'opacity 0.3s ease, transform 0.4s ease';
            targetImg.parentElement.appendChild(secondaryImg);
          }

          if (secondaryImg) {
            secondaryImg.src = dataUrl;
            secondaryImg.removeAttribute('srcset');
          }
        }
      });
    }

    card.setAttribute('data-icarus-rendered-key', prefsKey);
    card.setAttribute('data-icarus-personalised', 'true');
  }

  /**
   * Main observer & trigger function - Executes instantly with zero lag
   */
  window.applyIcarusPersonalisation = function(forceImmediate, rootNode) {
    const prefsRaw = localStorage.getItem('icarus_prefs');
    if (!prefsRaw) return;

    let prefs;
    try {
      prefs = JSON.parse(prefsRaw);
    } catch (e) {
      return;
    }

    if (!prefs || (!prefs.name && !prefs.number && !prefs.initials)) {
      return;
    }

    const scope = rootNode || document;
    const cards = scope.querySelectorAll ? scope.querySelectorAll('[data-icarus-card], [data-product-card]') : [];
    if (!cards.length) return;

    const prefsKey = `${prefs.name || ''}_${prefs.number || ''}_${prefs.initials || ''}`;

    if (forceImmediate) {
      // Execute all immediately in parallel
      cards.forEach(card => personaliseCard(card, prefs));
      return;
    }

    cards.forEach(card => {
      // Always personalise immediately inside modals, search drawers, grids, and dialogs
      if (card.closest('dialog, dialog-component, .search-modal, #search-modal, .predictive-search, .predictive-search-dropdown, .icarus-category, .icarus-product-grid')) {
        personaliseCard(card, prefs);
        return;
      }

      if (card.getAttribute('data-icarus-rendered-key') === prefsKey) {
        return;
      }

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries, obs) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              personaliseCard(entry.target, prefs);
            }
          });
        }, {
          rootMargin: '400px 0px',
          threshold: 0.01
        });
        observer.observe(card);
      } else {
        personaliseCard(card, prefs);
      }
    });
  };

  // MutationObserver for dynamic search results & filters
  let mutationDebounceTimer = null;
  const domObserver = new MutationObserver((mutations) => {
    let hasRelevantNodes = false;
    for (const mutation of mutations) {
      if (mutation.addedNodes && mutation.addedNodes.length) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            if (node.matches && (node.matches('[data-icarus-card], [data-product-card], .predictive-search-results__card') || 
                node.querySelector('[data-icarus-card], [data-product-card]'))) {
              hasRelevantNodes = true;
              break;
            }
          }
        }
      }
      if (hasRelevantNodes) break;
    }

    if (hasRelevantNodes) {
      clearTimeout(mutationDebounceTimer);
      mutationDebounceTimer = setTimeout(() => {
        window.applyIcarusPersonalisation(true);
      }, 16);
    }
  });

  function initObserver() {
    if (document.body) {
      domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Prewarm image cache and initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initObserver();
      window.prewarmIcarusPersonalisation();
      window.applyIcarusPersonalisation(false);
    });
  } else {
    initObserver();
    window.prewarmIcarusPersonalisation();
    window.applyIcarusPersonalisation(false);
  }

  // Search modal & drawer instant listeners
  document.addEventListener('dialog:open', () => window.applyIcarusPersonalisation(true));
  document.addEventListener('click', (e) => {
    if (e.target && e.target.closest && e.target.closest('#search-modal, .search-modal, [aria-controls="search-modal"], .predictive-search, .search-input, button[on\\:click*="search"]')) {
      window.applyIcarusPersonalisation(true);
    }
  });
  document.addEventListener('input', (e) => {
    if (e.target && e.target.closest && e.target.closest('.search-input, .predictive-search-form')) {
      window.applyIcarusPersonalisation(true);
    }
  });

  document.addEventListener('icarus:prefs-saved', () => window.applyIcarusPersonalisation(true));
  window.addEventListener('load', () => {
    window.prewarmIcarusPersonalisation();
    window.applyIcarusPersonalisation(false);
  });
  document.addEventListener('shopify:section:load', () => window.applyIcarusPersonalisation(true));

  // --- GLOBAL WISHLIST ENGINE ---
  window.IcarusWishlist = {
    STORAGE_KEY: 'icarus_wishlist_items',
    get() {
      try {
        return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },
    save(items) {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
        const count = items.length;
        if (typeof window.updateGlobalWishlistBadges === 'function') {
          window.updateGlobalWishlistBadges(count);
        }
        document.dispatchEvent(new CustomEvent('icarus:wishlist:update', {
          bubbles: true,
          detail: { items: items, wishlist: items, count: count }
        }));
      } catch (e) {
        console.error('Wishlist save error:', e);
      }
    },
    has(id) {
      if (!id) return false;
      const items = this.get();
      return items.some(item => String(item.id) === String(id));
    },
    add(productData) {
      if (!productData || !productData.id) return false;
      let items = this.get();
      const index = items.findIndex(item => String(item.id) === String(productData.id));
      if (index === -1) {
        items.unshift(productData);
        this.save(items);
        return true;
      }
      return false;
    },
    remove(id) {
      if (!id) return false;
      let items = this.get();
      const newItems = items.filter(item => String(item.id) !== String(id));
      this.save(newItems);
      return true;
    },
    toggle(productData) {
      if (!productData || !productData.id) return false;
      let items = this.get();
      const index = items.findIndex(item => String(item.id) === String(productData.id));
      let isAdded = false;
      if (index !== -1) {
        items.splice(index, 1);
      } else {
        items.unshift(productData);
        isAdded = true;
      }
      this.save(items);
      return isAdded;
    },
    clear() {
      this.save([]);
    }
  };

  // --- GLOBAL WISHLIST & CART FLYING ANIMATION ENGINE ---
  window.updateGlobalWishlistBadges = function(count) {
    if (typeof count === 'undefined' && window.IcarusWishlist) {
      count = window.IcarusWishlist.get().length;
    }
    const num = parseInt(count, 10) || 0;
    const badges = document.querySelectorAll('[data-header-wishlist-count], [data-drawer-wishlist-count]');
    badges.forEach(b => {
      b.textContent = num;
      if (b.hasAttribute('data-header-wishlist-count')) {
        b.style.display = num > 0 ? 'inline-flex' : 'none';
        b.style.opacity = num > 0 ? '1' : '0';
      }
    });
  };

  // Sync badges on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.updateGlobalWishlistBadges());
  } else {
    window.updateGlobalWishlistBadges();
  }
  document.addEventListener('icarus:wishlist:update', (e) => {
    const count = (e.detail && typeof e.detail.count !== 'undefined') ? e.detail.count : undefined;
    window.updateGlobalWishlistBadges(count);
  });

  window.icarusExecuteFlyAnimation = function(options) {
    const sourceElement = options.sourceElement;
    const imageUrl = options.imageUrl;
    const type = options.type || 'wishlist';
    const onComplete = options.onComplete;

    if (!sourceElement) return;

    sourceElement.classList.remove('ic-btn-pop');
    void sourceElement.offsetWidth;
    sourceElement.classList.add('ic-btn-pop');

    const targetSelector = (type === 'wishlist')
      ? '[data-wishlist-trigger], .header-wishlist-trigger, .header-actions__wishlist-icon, [data-header-wishlist-count], a[href="/pages/wishlist"]'
      : '.header-actions__cart-icon, cart-icon, [data-testid="cart-icon"], .header__icon--cart, .cart-icon-bubble, a[href="/cart"]';

    const targetEl = document.querySelector(targetSelector);
    const endRect = targetEl ? targetEl.getBoundingClientRect() : null;
    const startRect = sourceElement.getBoundingClientRect();
    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;

    let endX, endY;
    const isMobile = window.innerWidth <= 768;

    if (endRect && endRect.width > 0 && endRect.height > 0 && endRect.top >= -20 && endRect.top < window.innerHeight && endRect.left >= 0 && endRect.left <= window.innerWidth) {
      endX = endRect.left + endRect.width / 2;
      endY = endRect.top + endRect.height / 2;
    } else {
      endX = (type === 'wishlist') ? Math.max(30, window.innerWidth - 82) : Math.max(30, window.innerWidth - 36);
      endY = 32;
    }

    const flyerRadius = 26;
    const flyer = document.createElement('div');
    flyer.className = 'ic-flyer-badge ic-' + type + '-flyer';
    flyer.style.left = '0px';
    flyer.style.top = '0px';
    flyer.style.transform = 'translate3d(' + (startX - flyerRadius) + 'px, ' + (startY - flyerRadius) + 'px, 0) scale(0.6) rotate(-8deg)';
    flyer.style.opacity = '0';

    if (imageUrl) {
      flyer.style.backgroundImage = 'url(' + imageUrl + ')';
    } else {
      if (type === 'wishlist') {
        flyer.innerHTML = '<svg width="26" height="26" viewBox="0 0 24 24" fill="#ff4b55" stroke="#ff4b55" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
      } else {
        flyer.innerHTML = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2"><path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>';
      }
    }

    document.body.appendChild(flyer);

    const curveDirection = startX > (window.innerWidth / 2) ? 1 : -1;
    const lateralShift = isMobile ? (curveDirection * Math.min(50, window.innerWidth * 0.12)) : 25;
    
    const mid1X = startX + (endX - startX) * 0.2 + lateralShift;
    const mid1Y = startY - Math.min(90, Math.abs(startY - endY) * 0.3);
    const mid2X = startX + (endX - startX) * 0.65;
    const mid2Y = Math.max(12, Math.min(startY, endY) - (isMobile ? 30 : 45));

    const duration = isMobile ? 700 : 620;

    const startOriginX = startX - flyerRadius;
    const startOriginY = startY - flyerRadius;
    const mid1OriginX = mid1X - flyerRadius;
    const mid1OriginY = mid1Y - flyerRadius;
    const mid2OriginX = mid2X - flyerRadius;
    const mid2OriginY = mid2Y - flyerRadius;
    const mid3OriginX = (endX + (mid2X - endX) * 0.15) - flyerRadius;
    const mid3OriginY = (endY + (mid2Y - endY) * 0.15) - flyerRadius;
    const endOriginX = endX - flyerRadius;
    const endOriginY = endY - flyerRadius;

    if (flyer.animate) {
      const anim = flyer.animate([
        { offset: 0, transform: 'translate3d(' + startOriginX + 'px, ' + startOriginY + 'px, 0) scale(0.6) rotate(-8deg)', opacity: 0 },
        { offset: 0.14, transform: 'translate3d(' + mid1OriginX + 'px, ' + mid1OriginY + 'px, 0) scale(1.35) rotate(0deg)', opacity: 1 },
        { offset: 0.5, transform: 'translate3d(' + mid2OriginX + 'px, ' + mid2OriginY + 'px, 0) scale(1.12) rotate(' + (curveDirection * 10) + 'deg)', opacity: 1 },
        { offset: 0.82, transform: 'translate3d(' + mid3OriginX + 'px, ' + mid3OriginY + 'px, 0) scale(0.75) rotate(0deg)', opacity: 0.95 },
        { offset: 1, transform: 'translate3d(' + endOriginX + 'px, ' + endOriginY + 'px, 0) scale(0.2) rotate(0deg)', opacity: 0 }
      ], {
        duration: duration,
        easing: 'cubic-bezier(0.2, 0.8, 0.25, 1)',
        fill: 'forwards'
      });

      anim.onfinish = function() {
        if (flyer.parentNode) flyer.parentNode.removeChild(flyer);
        if (targetEl) {
          targetEl.classList.remove('ic-cart-impact');
          void targetEl.offsetWidth;
          targetEl.classList.add('ic-cart-impact');
          setTimeout(function() { targetEl.classList.remove('ic-cart-impact'); }, 600);
        }
        if (typeof onComplete === 'function') onComplete();
      };
    } else {
      setTimeout(function() {
        if (flyer.parentNode) flyer.parentNode.removeChild(flyer);
        if (typeof onComplete === 'function') onComplete();
      }, duration);
    }
  };

  window.icarusFlyToWishlist = function(sourceElement, imageUrl, onComplete) {
    window.icarusExecuteFlyAnimation({
      sourceElement: sourceElement,
      imageUrl: imageUrl,
      type: 'wishlist',
      onComplete: onComplete
    });
  };
})();
